import { ConfigStateService } from '@abp/ng.core';
import { Component, OnInit, OnDestroy, Inject, Renderer2, ViewChild, HostListener } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { MetroToastService } from '../shared/services/metro-toast.service';
import { ShoppingCartDto, ShoppingCartItemDto, ShoppingCartService } from '@proxy/shopping-carts';
import { SelectedSommelierService } from '../shared/services/selected-sommelier.service';
import { WineService } from '@proxy/wines';
import { StorageService } from '../shared/services/storage.service';
import { CartStateService } from '../shared/services/cart-state.service';

import { OrderService } from '@proxy/orders';
import { CouponInputComponent } from '../shared/components/coupon-input/coupon-input.component';

@Component({
  selector: 'app-shopping-cart',
  templateUrl: './shopping-cart.component.html',
  standalone: false
})
export class ShoppingCartComponent implements OnInit, OnDestroy {
  @ViewChild(CouponInputComponent) couponInput?: CouponInputComponent;
  cartItems: ShoppingCartItemDto[] = [];
  isDesktop = false;
  private readonly bodyScrollClass = 'cart-scroll-hidden';
  quantities: { [wineId: number]: number } = {};
  loadingStates: { [wineId: number]: boolean } = {};
  wineStock: { [wineId: number]: number } = {}; // Para almacenar la información de stock de cada vino
  taxRate: number = 0.21;
  userId: string;
  sommelierSlug: string;
  sommelierId: number;
  sommelierUserId?: string;

  // Coupon state
  appliedCoupon?: any;
  appliedCouponCode: string = '';
  appliedCouponDisplay: string = '';
  discountAmount: number = 0;

  constructor(private shoppingCartService: ShoppingCartService,
    private config: ConfigStateService,
    private router: Router,
    private selectedSommelierService: SelectedSommelierService,
    private wineService: WineService,
    private ordersService: OrderService,
    private toasterService: MetroToastService,
    private storageService: StorageService,
    private cartStateService: CartStateService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
  }

  ngOnInit(): void {
    this.updateViewportMode();
    this.renderer.addClass(this.document.body, this.bodyScrollClass);
    this.config.getOne$('currentUser').subscribe(res => {
      this.userId = res?.id;

      this.selectedSommelierService.getSlug$().subscribe(res => {
        this.sommelierSlug = res;
        
        // Cargar sommelierId para validación de cupones
        if (this.sommelierSlug) {
          this.ordersService.getSommelierBySlugBySlug(this.sommelierSlug).subscribe(sommelier => {
            this.sommelierId = sommelier.id;
            this.sommelierUserId = sommelier.userId;
          });
        }
      });

      if(this.userId != null){
        this.shoppingCartService.getCart(this.userId, this.sommelierSlug).subscribe(cart => {
          this.loadCartItems(cart);
        });
      }

    });
  }

  @HostListener('window:resize')
  updateViewportMode(): void {
    this.isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
  }

  onCouponApplied(result: any) {
    this.appliedCoupon = result;
    if (result.coupon) {
      this.appliedCouponCode = result.coupon.code;
      this.appliedCouponDisplay = result.coupon.discountType === 0 
        ? `${result.coupon.discountValue}%` 
        : result.coupon.code;
    } else {
      this.appliedCouponCode = '';
      this.appliedCouponDisplay = '';
    }
    this.discountAmount = result.discountAmount;
  }

  onCouponRemoved() {
    this.appliedCoupon = undefined;
    this.appliedCouponCode = '';
    this.appliedCouponDisplay = '';
    this.discountAmount = 0;
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.document.body, this.bodyScrollClass);
  }

  async updateQuantity(data: {quantity: number, loading: boolean, checkForNewStock?: boolean}, wineId: number) {
    const qty = data.quantity;

    // Si se está verificando si hay nuevo stock disponible
    if (data.checkForNewStock) {
      this.loadingStates[wineId] = true;

      try {
        // Refrescar el stock del vino
        const newStock = await this.refreshWineStock(wineId);

        // Verificar si hay suficiente stock para incrementar
        const itemIndex = this.cartItems.findIndex(item => item.wineId === wineId);
        if (itemIndex !== -1) {
          if (newStock > this.cartItems[itemIndex].quantity) {
            // Hay nuevo stock disponible, incrementar la cantidad
            const newQuantity = this.cartItems[itemIndex].quantity + 1;
            this.cartItems[itemIndex].quantity = newQuantity;

            // Enviar actualización al backend
            this.shoppingCartService.updateItemQuantity(this.userId, wineId, newQuantity)
              .subscribe({
                next: () => {
                  // Actualización exitosa, quitar estado de carga
                  this.loadingStates[wineId] = false;
                  const total = this.cartItems.reduce((sum, i) => sum + i.quantity, 0);
                  this.cartStateService.setCount(total);
                  if (this.appliedCoupon) {
                    this.couponInput?.removeCoupon();
                  }
                },
                error: err => {
                  console.error(err);
                  // Si hay un error, revertir el cambio local
                  if (itemIndex !== -1) {
                    this.cartItems[itemIndex].quantity = this.quantities[wineId] || this.cartItems[itemIndex].quantity;
                  }
                  // Quitar estado de carga incluso en caso de error
                  this.loadingStates[wineId] = false;
                }
              });
          } else {
            // No hay nuevo stock disponible, simplemente quitar el estado de carga
            this.loadingStates[wineId] = false;
          }
        }
      } catch (err) {
        console.error('Error al refrescar el stock:', err);
        this.loadingStates[wineId] = false;
      }
    } else {
      // Actualizar el estado de carga para este item específico
      this.loadingStates[wineId] = true;

      // Actualizar localmente la cantidad en el item correspondiente
      const itemIndex = this.cartItems.findIndex(item => item.wineId === wineId);
      if (itemIndex !== -1) {
        this.cartItems[itemIndex].quantity = qty;

        // Enviar actualización al backend
        this.shoppingCartService.updateItemQuantity(this.userId, wineId, qty)
          .subscribe({
            next: () => {
              // Actualización exitosa, quitar estado de carga
              this.loadingStates[wineId] = false;

              const total = this.cartItems.reduce((sum, i) => sum + i.quantity, 0);
              this.cartStateService.setCount(total);
              if (this.appliedCoupon) {
                this.couponInput?.removeCoupon();
              }
            },
            error: err => {
              console.error(err);
              // Si hay un error, revertir el cambio local
              if (itemIndex !== -1) {
                this.cartItems[itemIndex].quantity = this.quantities[wineId] || this.cartItems[itemIndex].quantity;
              }
              // Quitar estado de carga incluso en caso de error
              this.loadingStates[wineId] = false;
            }
          });
      }
    }
  }

  removeItemFromCart(data: {quantity: number, loading: boolean}, wineId: number) {
    // Actualizar el estado de carga para este item específico
    this.loadingStates[wineId] = true;

    // Eliminar del carrito con hard delete
    this.shoppingCartService.removeItemHard(this.userId, wineId)
      .subscribe({
        next: () => {
          // Actualizar localmente la lista de items
          this.cartItems = this.cartItems.filter(item => item.wineId !== wineId);
          // Quitar estado de carga
          this.loadingStates[wineId] = false;
          // Mostrar toast de éxito después de que se complete la operación
          this.toasterService.success('::ItemRemovedFromCart', '::Removed');

           const total = this.cartItems.reduce((sum, i) => sum + i.quantity, 0);
          this.cartStateService.setCount(total);
          if (this.appliedCoupon) {
            this.couponInput?.removeCoupon();
          }
        },
        error: err => {
          console.error(err);
          // Quitar estado de carga incluso en caso de error
          this.loadingStates[wineId] = false;
          // Mostrar mensaje de error si es necesario
          this.toasterService.error('::FailedToRemoveItem', '::Error');
        }
      });
  }

  deleteItemFromCart(data: {quantity: number, loading: boolean}, wineId: number) {
    // Actualizar el estado de carga para este item específico
    this.loadingStates[wineId] = true;

    // Eliminar del carrito con hard delete
    this.shoppingCartService.removeItemHard(this.userId, wineId)
      .subscribe({
        next: () => {
          // Actualizar localmente la lista de items
          this.cartItems = this.cartItems.filter(item => item.wineId !== wineId);
          // Quitar estado de carga
          this.loadingStates[wineId] = false;
          // Mostrar toast de éxito después de que se complete la operación
          this.toasterService.success('::ItemRemovedFromCart', '::Removed');

          const total = this.cartItems.reduce((sum, i) => sum + i.quantity, 0);
          this.cartStateService.setCount(total);
          if (this.appliedCoupon) {
            this.couponInput?.removeCoupon();
          }
        },
        error: err => {
          console.error(err);
          // Quitar estado de carga incluso en caso de error
          this.loadingStates[wineId] = false;
          // Mostrar mensaje de error si es necesario
          this.toasterService.error('::FailedToRemoveItem', '::Error');
        }
      });
  }

  // Método para refrescar el stock de un vino específico
  refreshWineStock(wineId: number): Promise<number> {
    return new Promise((resolve, reject) => {
      this.wineService.get(wineId).subscribe({
        next: (wine) => {
          this.wineStock[wineId] = wine.stock;
          resolve(wine.stock);
        },
        error: (err) => {
          console.error(`Error al refrescar el stock para el vino ${wineId}:`, err);
          reject(err);
        }
      });
    });
  }

  // Método para verificar disponibilidad de stock para todos los items del carrito y ajustar si es necesario
  async checkFinalStockAvailability(): Promise<{hasSufficientStock: boolean, adjustedItems: {wineId: number, oldQuantity: number, newQuantity: number, wineName: string}[]}> {
    const adjustedItems: {wineId: number, oldQuantity: number, newQuantity: number, wineName: string}[] = [];

    // Iterar sobre todos los items en el carrito
    for (const item of this.cartItems) {
      try {
        // Obtener la información actualizada del stock para este vino
        const wineInfo = await this.refreshWineStock(item.wineId);

        // Verificar si hay suficiente stock para la cantidad solicitada
        if (wineInfo < item.quantity) {
          console.warn(`No hay suficiente stock para ${item.wineName}. Disponible: ${wineInfo}, Solicitado: ${item.quantity}`);

          // Ajustar la cantidad al stock disponible
          const oldQuantity = item.quantity;
          item.quantity = wineInfo;
          // Registrar el ajuste
          adjustedItems.push({
            wineId: item.wineId,
            oldQuantity: oldQuantity,
            newQuantity: wineInfo,
            wineName: item.wineName
          });
          // Actualizar la cantidad en el backend también
          if (wineInfo > 0) {
            await new Promise<void>((resolve, reject) => {
              this.shoppingCartService.updateItemQuantity(this.userId, item.wineId, wineInfo)
                .subscribe({
                  next: () => resolve(),
                  error: (err) => {
                    console.error(`Error al actualizar la cantidad para el vino ${item.wineId}:`, err);
                    reject(err);
                  }
                });
            });
          } else {
            // Si el stock es 0, eliminar el ítem del carrito
            await new Promise<void>((resolve, reject) => {
              this.shoppingCartService.removeItem(this.userId, item.wineId)
                .subscribe({
                  next: () => {
                    // Remover el ítem del carrito local
                    const index = this.cartItems.findIndex(cartItem => cartItem.wineId === item.wineId);
                    if (index !== -1) {
                      this.cartItems.splice(index, 1);
                    }
                    resolve();
                  },
                  error: (err) => {
                    console.error(`Error al eliminar el vino ${item.wineId} del carrito:`, err);
                    reject(err);
                  }
                });
            });
          }
        }
      } catch (error) {
        console.error(`Error al verificar stock para el vino ${item.wineId}:`, error);
        // En caso de error al verificar el stock, no permitimos continuar
        return {hasSufficientStock: false, adjustedItems: []};
      }
    }

    this.cartStateService.setCount(this.cartItems.reduce((sum, i) => sum + i.quantity, 0));

    // Si llegamos hasta aquí, hay suficiente stock para todos los items (posiblemente ajustados)
    return {hasSufficientStock: true, adjustedItems};
  }

  get total(): number {
    const total = this.cartItems.reduce((sum, item) =>
      sum + item.unitPrice * (this.quantities[item.wineId] || item.quantity), 0);
    return total;
  }

  get totalWithDiscount(): number {
    const total = this.total - this.discountAmount;
    return total > 0 ? total : 0;
  }

  get tax(): number {
    return this.totalWithDiscount * (this.taxRate / (1 + this.taxRate));
  }

  get subtotal(): number {
    return this.totalWithDiscount - this.tax;
  }


async goToOrderConfirm() {
  // Verificar que haya stock suficiente para todos los ítems antes de continuar
  const result = await this.checkFinalStockAvailability();

  // Si hay items ajustados, notificar al usuario
  if (result.adjustedItems.length > 0) {
    let message = '::CartInsufficientStockMessage\n';
    result.adjustedItems.forEach(item => {
      if (item.newQuantity > 0) {
        message += `- ${item.wineName}: ${item.oldQuantity} → ${item.newQuantity}\n`;
      } else {
        message += `- ${item.wineName}: ::RemovedOutOfStock\n`;
      }
    });

    this.toasterService.warn(message, '::StockUpdated');
  }

  // Si no hay stock suficiente para continuar (caso de error en la verificación), impedir la continuación
  if (!result.hasSufficientStock) {
    this.toasterService.error('::CartAdjustmentError', '::Error');
    return;
  }

  // Verificar si aún hay items en el carrito antes de continuar
  if (this.cartItems.length === 0) {
    this.toasterService.error('::CartEmptyAfterAdjustment', '::EmptyCart');
    return;
  }

  this.selectedSommelierService.navigate(['order-checkout'], {
    state: { 
      items: this.cartItems,
      appliedCouponCode: this.appliedCouponCode,
      appliedCouponDisplay: this.appliedCouponDisplay,
      discountAmount: this.discountAmount
    }
  });
}


  loadCartItems(cart: ShoppingCartDto) {
  this.cartStateService.setCount(cart.totalItems ?? this.cartItems.length);

    this.cartItems = cart.items.map(item => ({
      ...item,
      imageUrl: null
    }));

    this.cartItems.forEach(item => {
      // Cargar la imagen del vino
      if (item.wineFileId) {
        this.storageService.getImage(item.wineFileId, 100,200).subscribe(blob => {
          item.imageUrl = URL.createObjectURL(blob);
        });
      } else {
        item.imageUrl = 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTrKz7M8DJcvzguBhnOjQq_xUVpv_bxz26ycg&s';
      }

      // Cargar la información de stock para este vino
      this.wineService.get(item.wineId).subscribe({
        next: (wine) => {
          this.wineStock[item.wineId] = wine.stock;
        },
        error: (err) => {
          // En caso de error, asumimos stock 0 para prevenir problemas
          this.wineStock[item.wineId] = 0;
        }
      });
    });
  }
}
